import {
  ChangeDetectorRef,
  Component,
  OnInit,
  QueryList,
  ViewChildren,
  ViewChild,
} from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { UiStateService } from "../core/services/ui-state-service";
import { filter, takeUntil, tap } from "rxjs/internal/operators";
import { BaseComponent } from "../shared/base.component";
import { NeutModel, NeutUtils, VOORSPONNING_SIZE_MAP } from "../shared/models/neut.model";
import { NeutComponent } from "./neut/neut.component";
import { NeutInput } from "./neut/neut-input";
import { BackendService } from "../core/services/backend.service";
import { NeutDto } from "../shared/api-models/neutDto";
import { ModelFactory } from "../shared/models/model-factory";
import { NeutPosition } from "./neut/neut-position";

import { DaagmaatComponent } from "./daagmaat/daagmaat.component";
import { GlaslatDto } from "../shared/api-models/glaslatDto";

import { FormGroup, FormBuilder } from "@angular/forms";


@Component({
  selector: "luv-neuten",
  templateUrl: "./neuten-horiz.component.html",
  styleUrls: ["./neuten.component.scss"],
})
export class NeutenComponent extends BaseComponent implements OnInit {
  public neutInputModels: NeutInput[] = [];

  @ViewChildren("neut") neutComponents: QueryList<NeutComponent>;
  @ViewChildren("daagmaat") daagmatComponents: QueryList<DaagmaatComponent>;
  @ViewChild("daagmaatA") daagmatAComponent: DaagmaatComponent;

  public currentDorpelCode: string;
  public currentDorpelType: "Binnendorpel" | "Buitendorpel" | "Hefschuifdorpel";
  public isCurrentDoorSliding: boolean;
  public count: number = 0;
  private neuten: NeutDto[] = [];
  private filteredGlaslatten: GlaslatDto[] = [];
  public canShowRailOptions: boolean = false;
  public isNeutenSectionInvalid: boolean = false;
  public showDealerContactMessage: boolean = false;
  public currentSchema: string = null; //TODO: why set this here and pass down to neuten when in alternative this could be read off the uistate
  public formGroup: FormGroup;
  public showSponningmaat: boolean = false;

  public constructor(
    private uiStateService: UiStateService,
    private backendService: BackendService,
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    await this.handleInitialValues();
    this.formGroup = this.buildForm();

    this.setCurrentDorpelType();
    this.updateNeutInputModels();

    this.uiStateService.dorpelCode$
      .pipe(filter((code) => !!code))
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(
        tap((dorpelCode) => {
          this.currentDorpelCode = dorpelCode;
          this.setCurrentDorpelType();
          this.updateNeutInputModels();
          this.toggleRailTypeEnabledState(false);
          this.resetRailType();
        })
      )
      .subscribe((code) => {});

    this.uiStateService.schema$
      .pipe(filter((schema) => !!schema))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((schema) => {
        this.currentSchema = schema;
        this.toggleRailTypeEnabledState(!!schema)
      });

    this.uiStateService.neutenCount$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((count) => {
        if (!count) {
          /* moved validation here (removed filter pipe) cuz we want the uiStateService 
          to notify even if values are empty or 0 
          (to invalidate certain fields in other components) */
          this.isNeutenSectionInvalid = true;
          this.showDealerContactMessage = false;
          return;
        }

        this.count = count;
        if (count === 1 && !this.isCurrentDoorSliding) {
          this.isNeutenSectionInvalid = false;
          this.showDealerContactMessage = true;
        } else {
          this.isNeutenSectionInvalid = false;
          this.showDealerContactMessage = false;
        }
        this.addOrRemoveNeut();
        this.cdRef.detectChanges();
      });
  }

  private toggleRailTypeEnabledState(isEnabled: boolean) {
    if (isEnabled) {
      this.formGroup.controls["railType"].enable();
    } else {
      this.formGroup.controls["railType"].disable();
    }
  }

  private resetRailType(): void {
    this.formGroup.get("railType").setValue("");
    this.uiStateService.setRailType("");
  }

  private getNeuten(isPrefilledOrder: boolean): Observable<NeutDto[]> {
    let neutDtos: NeutDto[] = [];
    neutDtos = this.uiStateService.neutenChoices;
    return isPrefilledOrder ? of(neutDtos) : this.backendService.getNeuten();
  }

  private setCurrentDorpelType() {
    if (!this.currentDorpelCode) {
      return;
    }
    const dorpelCode = this.currentDorpelCode;
    const chosenDorpel = this.uiStateService.dorpelChoices.find(
      (d) => d.dorpelCode === dorpelCode
    );
    this.currentDorpelType = chosenDorpel.dorpelsoort;
    this.isCurrentDoorSliding = this.currentDorpelType === "Hefschuifdorpel";
    this.canShowRailOptions = chosenDorpel.dorpelCode.toLocaleLowerCase().indexOf("hs.190") !== -1;
  }

  public getNeutenFormData(): NeutModel[] {
    const models = this.neutComponents
      .toArray()
      .map((comp) => <NeutModel>{
        ...comp.formGroup.value,
        linksVoorsponning: VOORSPONNING_SIZE_MAP[comp.formGroup.value.linksVoorsponning],
        rechtsVoorsponning: VOORSPONNING_SIZE_MAP[comp.formGroup.value.rechtsVoorsponning],
        customWidth: comp.specialNeutWidth
      });
    models.forEach((model) => {
      const neutDto = this.neuten.find((n) => n.neutCode === model.neut);
      if (neutDto) {
        model.omschrijving = neutDto.omschrijving;
      }
    });

    if (!this.isCurrentDoorSliding) {
      //for sliding doors we have no daagmaats and only one neut
      const modelDaagmaatA = <NeutModel>this.daagmatAComponent.formGroup.value;
      models[0].maatA = modelDaagmaatA.maatA || 0;

      const daagmatCompList = this.daagmatComponents.toArray();
      for (let i = 0; i < models.length; i++) {
        const daagmatNeutModel = <NeutModel>daagmatCompList[i].formGroup.value;
        models[i].daagmaat = daagmatNeutModel.daagmaat;
        models[i].sponningsoort = daagmatNeutModel.sponningsoort;
        models[i].sponningmaat = daagmatNeutModel.sponningmaat;
        models[i].glaslat = daagmatNeutModel.glaslat;
        models[i].beglazingsprofiel = daagmatNeutModel.beglazingsprofiel;
        models[i].aanslagprofiel = daagmatNeutModel.aanslagprofiel;
        models[i].gemonteerd = daagmatNeutModel.gemonteerd;
        models[i].damverbreeding = daagmatNeutModel.damverbreeding;
        models[i].dubbelAanslagprofiel = daagmatNeutModel.dubbelAanslagprofiel;
        if (i === models.length - 1) {
          models[i].maatZ = daagmatNeutModel.maatZ || 0;
        }
      }
    }

    return models;
  }

  public validateNeutenComponents(): boolean {
    let foundInvalidComponents: boolean = false;
    const models = this.neutComponents
      .toArray()
      .map((comp) => <NeutModel>comp.formGroup.value);
    models.forEach((model) => {
      const neutDto = this.neuten.find((n) => n.neutCode === model.neut);
      if (neutDto && !neutDto.isInHouseNeut) {
        foundInvalidComponents = true;
      }
    });

    return !foundInvalidComponents;
  }

  public dataIsValid(): boolean {
    const isValid = !this.neutComponents
      .toArray()
      .some((form) => form.formGroup.invalid);

    if (!isValid) {
      this.neutComponents.toArray().forEach((nc) => {
        const formGroup = nc.formGroup;
        Object.keys(formGroup.controls).forEach((field) => {
          const control = formGroup.get(field);
          control.markAsTouched();
        });
      });
    }

    return isValid;
  }

  public onNeutVoorsponningChanged(data): void {
    let targetNeutComponent =
      this.neutComponents.toArray()[data.targetNeutIndex];

    if (!targetNeutComponent) {
      return;
    }

    if (data.targetProperty === "linksVoorsponning") {
      targetNeutComponent.setLinksVoorsponningValue(data.value);
    } else if (data.targetProperty === "rechtsVoorsponning") {
      targetNeutComponent.setRechtsVoorsponningValue(data.value);
    }
  }

  public onNeutCodeChanged(neutData: any, index): void {
    // this.resetNeutInputModel(index);
    this.updateSpecificNeutInputModel(neutData, index);
  }

  private updateSpecificNeutInputModel(neutData: any, index) {
    let target = this.neutInputModels[index];

    if (target) {
      target.model.omschrijving = neutData.neutDescription;
      target.model.neut = neutData.neutCode;
      target.model.aanslagprofiel = neutData.aanslagprofiel;
      target.model.beglazingsprofiel = neutData.beglazingsprofiel;
      target.model.glaslat = neutData.glaslat;
      target.model.gemonteerd = neutData.gemonteerd;
      target.model.damverbreeding = neutData.damverbreeding;
      target.model.dubbelAanslagprofiel = neutData.dubbelAanslagprofiel;
    }
  }

  private addOrRemoveNeut() {
    const dorpel = this.uiStateService.currentDorpel;

    if (this.neutInputModels.length < this.count) {
      const dif = this.count - this.neutInputModels.length;
      for (let i = 0; i < dif; i++) {
        const neutOrder = ModelFactory.createNeut();
        const input = {
          neutenChoices: this.neuten,
          glaslattenChoices: this.filteredGlaslatten,
          model: <NeutModel>{
            daagmaat: neutOrder.daagmaat,
            maatA: dorpel.maatA,
            maatZ: dorpel.maatZ,
            neut: neutOrder.neutCode,
            sponningmaat: neutOrder.sponningmaat,
            sponningsoort: neutOrder.sponningsoort,
            glaslat: neutOrder.glaslat,
            damverbreeding: neutOrder.damverbreeding,
            beglazingsprofiel: neutOrder.beglazingsprofiel,
            aanslagprofiel: neutOrder.aanslagprofiel,
            gemonteerd: neutOrder.gemonteerd,
            dubbelAanslagprofiel: neutOrder.dubbelAanslagprofiel,
            position: NeutPosition.Center,
          },
        };
        // insert neut second to last, in the center position
        this.neutInputModels.splice(this.neutInputModels.length - 1, 0, input);
        this.neutInputModels.forEach(
          (input, index) =>
            (input.model.position = NeutUtils.getPositionFromIndex(
              index,
              this.neutInputModels.length
            ))
        );
        this.neutInputModels = [...this.neutInputModels];
      }
    } else if (this.neutInputModels.length > this.count) {
      const dif = this.neutInputModels.length - this.count;
      for (let i = 0; i < dif; i++) {
        this.neutInputModels.splice(this.neutInputModels.length - 2, 1);
        this.neutInputModels.forEach(
          (input, index) =>
            (input.model.position = NeutUtils.getPositionFromIndex(
              index,
              this.neutInputModels.length
            ))
        );
        this.neutInputModels = [...this.neutInputModels];
      }
    }
  }

  private buildForm(): FormGroup {
    const formGroup = this.formBuilder.group({
      railType: [{value: "", disabled: true}]
    });

    return formGroup;
  }

  public onRailTypeChange() {
    let railType = this.formGroup.get("railType").value;
    this.uiStateService.setRailType(railType);
  }

  // private resetNeutInputModel(index: number) {
  //   const dorpel = this.uiStateService.currentDorpel;
  //   let targetNeut = this.neutInputModels[index].model;

  //   if (targetNeut) {
  //     targetNeut.aanslagprofiel = undefined;
  //     targetNeut.beglazingsprofiel = undefined;
  //     targetNeut.glaslat = undefined;
  //     targetNeut.gemonteerd = undefined;
  //     targetNeut.damverbreeding = undefined;
  //     targetNeut.dubbelAanslagprofiel = undefined;
  //   }
  // }

  private updateNeutInputModels() {
    const dorpel = this.uiStateService.currentDorpel;
    const neuten = this.neuten;
    const glaslatten = this.filteredGlaslatten;
    const values = dorpel.neuten.map((neutOrder, index) => {
      const input = {
        neutenChoices: neuten,
        glaslattenChoices: glaslatten,
        model: <NeutModel>{
          daagmaat: neutOrder.daagmaat,
          maatA: dorpel.maatA || 0,
          maatZ: dorpel.maatZ || 0,
          neut: neutOrder.neutCode,
          sponningmaat: neutOrder.sponningmaat,
          sponningsoort: neutOrder.sponningsoort,
          glaslat: neutOrder.glaslat,
          aanslagprofiel: neutOrder.aanslagprofiel,
          beglazingsprofiel: neutOrder.beglazingsprofiel,
          gemonteerd: neutOrder.gemonteerd,
          damverbreeding: neutOrder.damverbreeding,
          dubbelAanslagprofiel: neutOrder.dubbelAanslagprofiel,
          omschrijving: neutOrder.neutDescription,
          linksVoorsponning: neutOrder.linksVoorsponning,
          rechtsVoorsponning: neutOrder.rechtsVoorsponning,
          position: NeutUtils.getPositionFromIndex(index, dorpel.neuten.length),
        },
      };
      return input;
    });

    values.forEach(
      (input, index) =>
        (input.model.position = NeutUtils.getPositionFromIndex(
          index,
          values.length
        ))
    );
    this.neutInputModels = values;
  }

  private filterGlaslattenChoicesByProductsoort(
    glaslatten: GlaslatDto[]
  ): GlaslatDto[] {
    let currentProductType: string = this.uiStateService.currentProductType;
    if (!currentProductType) {
      return glaslatten;
    }

    return glaslatten.filter(
      (glaslat) =>
        glaslat.productsoort.toLowerCase() === currentProductType.toLowerCase()
    );
  }

  private handleInitialValues(): Promise<void> {
    const dorpel = this.uiStateService.currentDorpel;
    const isPrefilledOrder = this.uiStateService.fromPrefilledOrder;
    return new Promise<void>((resolve, reject) => {
      forkJoin(
        this.getNeuten(isPrefilledOrder),
        this.backendService.getGlaslatten()
      )
        .pipe(
          tap(([neuten, glaslatten]) => {
            this.neuten = neuten;
            this.filteredGlaslatten =
              this.filterGlaslattenChoicesByProductsoort(glaslatten);
            this.uiStateService.setNeutenChoices(this.neuten);
          })
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(([neuten, glaslatten]) => {
          this.neuten = neuten;
          this.filteredGlaslatten =
            this.filterGlaslattenChoicesByProductsoort(glaslatten);
          this.updateNeutInputModels();
          resolve();
        });
    });
  }
}
